import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Member,
  CreateMember,
  UpdateMember,
  UpdateMemberStatus,
  MemberFilterParams,
  MemberCategory,
  MemberStatsResponse,
  MembersCategoryCount
} from '@/lib/api/types'
import { MemberStatsResponseSchema } from '@/lib/api/types'

export const memberService = {
  // Get all members
  getAllMembers: async (): Promise<ApiResponse<Member[]>> => {
    return await get<ApiResponse<Member[]>>('members/private')
  },

  // Filter members with pagination
  filterMembers: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: MemberFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Member>>> => {
    return await post<ApiResponse<PaginatedResponse<Member>>>(
      `members/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get member by ID
  getMemberById: async (memberId: string): Promise<ApiResponse<Member>> => {
    return await get<ApiResponse<Member>>(`members/private/${memberId}`)
  },

  // Create a new member
  createMember: async (data: CreateMember): Promise<ApiResponse<Member>> => {
    return await post<ApiResponse<Member>>(
      'members/admin/private',
      data
    )
  },

  // Update an existing member
  updateMember: async (
    memberId: string,
    data: UpdateMember
  ): Promise<ApiResponse<Member>> => {
    return await put<ApiResponse<Member>>(
      `members/admin/private/${memberId}`,
      data
    )
  },

  // Update member status
  updateMemberStatus: async (
    memberId: string,
    data: UpdateMemberStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `members/admin/private/${memberId}/status`,
      data
    )
  },

  // Delete a member
  deleteMember: async (memberId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `members/admin/private/${memberId}`
    )
  },

  // Get member categories
  getMemberCategories: async (): Promise<ApiResponse<MemberCategory[]>> => {
    return await get<ApiResponse<MemberCategory[]>>('members/private/member-category')
  },

  // Get published members count
  getPublishedMembersCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('members/private/stats/published')
  },

  // Get draft members count
  getDraftMembersCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('members/private/stats/draft')
  },

  // Get members dashboard stats
  getMembersStats: async (): Promise<MemberStatsResponse> => {
    const res = await get('members/private/stats')
    return MemberStatsResponseSchema.parse(res)
  },

  // Filter member count
  filterMembersCategoryCount: async (): Promise<ApiResponse<MembersCategoryCount>> => {
    return await post<ApiResponse<MembersCategoryCount>>(
      'members/private/stats/filter-count',
      {}
    )
  },

  // Search members
  searchMembers: async (
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<Member>>> => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString()
    })
    
    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<Member>>>(
      `members/private/search?${queryParams.toString()}`
    )
  }
}
